import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import LanguageSelector from '../components/LanguageSelector';
import Bilingual, { bilingualOverlayCode } from '../components/Bilingual';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APP_BASE = process.env.REACT_APP_APP_BASE_URL || 'https://geniusoneplus.com';

// Sprint 10.6 — Free sample lesson at /try. No auth. Kid-first.
// Sprint 10.6.5 — Adds the "amma nudge" completion card + funnel counter.
// Sprint 10.6.7 — Chairman override: bilingual display for `si` and `ta`
// locales. Local-language line on top, English line below in muted italic.
// English CTAs / badges are UI chrome and stay English regardless of locale.
//
export default function Try() {
  const { i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [completed, setCompleted] = useState(false);
  const audioRef = useRef(null);
  const nudgeRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/public/sample-lesson`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || 'Could not load the sample lesson'));
  }, []);

  const playAudio = () => {
    // Best-effort English audio playback for the title/hook. Audio stays
    // English in this build — re-recording in local languages is a future
    // sprint.
    const url = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/static/audio/en/scratch-01-your-first-talking-cat/title.mp3`;
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    const el = new Audio(url);
    audioRef.current = el;
    setAudioPlaying(true);
    el.addEventListener('ended', () => setAudioPlaying(false));
    el.addEventListener('error', () => setAudioPlaying(false));
    el.play().catch(() => setAudioPlaying(false));
  };

  const shareToWhatsApp = () => {
    const msg = encodeURIComponent(
      `Look what I made! Free coding lesson for kids: ${APP_BASE}/try — takes 15 mins.`
    );
    window.open(`https://wa.me/?text=${msg}`, '_blank', 'noopener');
  };

  const shareToAmmaWhatsApp = () => {
    const msg = encodeURIComponent(
      `Look what I built in 15 min! 🐱 [attach photo] — Free lesson: ${APP_BASE}/try — Full course LKR 750/mo`
    );
    window.open(`https://wa.me/?text=${msg}`, '_blank', 'noopener');
  };

  const markComplete = () => {
    if (completed) {
      nudgeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setCompleted(true);
    axios.post(`${API}/public/sample-lesson-completed`).catch(() => {});
    setTimeout(() => {
      nudgeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 60);
  };

  if (error) return <div className="p-8 text-red-600" data-testid="try-error">{error}</div>;
  if (!data) return <div className="p-8 text-sm text-muted-foreground" data-testid="try-loading">Loading…</div>;

  const l = data.lesson;
  const steps = l.steps || [];
  const lastStepNum = steps.length ? (steps[steps.length - 1].step || steps.length) : 0;

  // Bilingual overlay: pick si or ta block based on current UI locale.
  const overlayCode = bilingualOverlayCode(i18n.language);
  const tx = overlayCode ? (l.translations?.[overlayCode] || null) : null;
  const txSteps = tx?.steps || [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white" data-testid="try-page">
      <div className="max-w-3xl mx-auto px-4 py-10 space-y-6">
        {/* Sprint 10.6.7 — Language toggle on /try so parents can flip to
            සිංහල / தமிழ் and see the bilingual overlay. */}
        <div className="flex items-center justify-end" data-testid="try-lang-toggle-wrap">
          <LanguageSelector />
        </div>
        {tx && (
          <div
            className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900"
            data-testid="try-bilingual-notice"
          >
            {overlayCode === 'si'
              ? 'ද්විභාෂා දර්ශනය: සිංහල උඩ · English below · Audio stays English'
              : 'இருமொழி காட்சி: தமிழ் மேலே · English below · Audio stays English'}
          </div>
        )}

        <div className="relative mb-2" data-testid="try-hero-img-wrap">
          <div className="absolute inset-0 bg-amber-200/40 rounded-3xl -rotate-2" />
          <img
            src="/marketing/hero-11-kid-pointing-laptop.jpeg"
            alt="A kid proudly showing off a laptop screen to their parent"
            className="relative rounded-3xl shadow-xl w-full h-56 sm:h-72 object-cover"
            data-testid="try-hero-img"
          />
        </div>
        <div className="text-center">
          <Badge className="bg-amber-200 text-amber-900" data-testid="try-free-badge">
            🎁 Free · No signup · 15 minutes
          </Badge>
          <div className="mt-3">
            <Bilingual
              local={tx?.title}
              english={l.title}
              className="text-4xl sm:text-5xl font-bold"
              localAs="h1"
              englishAs="h1"
              testId="try-title"
            />
          </div>
          <div className="mt-2 max-w-2xl mx-auto">
            <Bilingual
              local={tx?.kid_hook && `“${tx.kid_hook}”`}
              english={`“${l.kid_hook}”`}
              className="text-lg text-slate-600 italic"
              testId="try-kid-hook"
            />
          </div>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <Button onClick={playAudio} data-testid="try-play-audio">
              {audioPlaying ? '⏸ Playing…' : '🔊 Listen to the intro'}
            </Button>
            <Button variant="outline" onClick={shareToWhatsApp} data-testid="try-share-wa">
              💬 Share with a friend
            </Button>
          </div>
        </div>

        <Card className="border-2 border-emerald-300" data-testid="try-wow-card">
          <CardHeader>
            <CardTitle className="text-lg">✨ The wow moment</CardTitle>
          </CardHeader>
          <CardContent className="text-slate-700">
            <Bilingual
              local={tx?.wow_moment}
              english={l.wow_moment}
              className="text-slate-700"
              testId="try-wow-body"
            />
          </CardContent>
        </Card>

        <div className="space-y-3">
          <h2 className="text-2xl font-semibold" data-testid="try-steps-heading">Steps ({steps.length})</h2>
          {steps.map((s, i) => {
            const n = s.step || i + 1;
            const isLast = n === lastStepNum;
            const txStep = txSteps[i] || null;
            return (
              <Card key={i} data-testid={`try-step-${n}`}>
                <CardHeader>
                  <CardTitle className="text-base flex items-baseline gap-3">
                    <span className="text-2xl text-amber-500 font-bold">{n}.</span>
                    <div className="flex-1">
                      <Bilingual
                        local={txStep?.instruction}
                        english={s.instruction || s.title || 'Step'}
                        className="font-semibold"
                        testId={`try-step-${n}-instruction`}
                      />
                    </div>
                  </CardTitle>
                  {(s.visual_hint || txStep?.visual_hint) && (
                    <CardDescription className="pl-8" data-testid={`try-step-${n}-hint`}>
                      <Bilingual
                        local={txStep?.visual_hint && `💡 ${txStep.visual_hint}`}
                        english={s.visual_hint ? `💡 ${s.visual_hint}` : ''}
                        className="italic"
                        testId={`try-step-${n}-hint-body`}
                      />
                    </CardDescription>
                  )}
                </CardHeader>
                {isLast && (
                  <CardContent className="pt-0">
                    <Button
                      size="lg"
                      onClick={markComplete}
                      className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold"
                      data-testid="try-complete-btn"
                    >
                      🐱 I made the cat talk! →
                    </Button>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>

        {completed && (
          <Card
            ref={nudgeRef}
            className="bg-gradient-to-br from-pink-50 to-amber-50 border-2 border-pink-300 shadow-lg"
            data-testid="try-amma-nudge-card"
          >
            <CardHeader>
              <CardTitle className="text-2xl text-center">
                🎉 You made a talking cat!
              </CardTitle>
              <CardDescription className="text-center text-slate-700 text-base pt-1">
                Show your amma — then unlock 40 more.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
              <Button
                size="lg"
                onClick={shareToAmmaWhatsApp}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                data-testid="try-amma-share-btn"
              >
                Show your amma 📱
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50 font-semibold"
                data-testid="try-amma-unlock-btn"
              >
                <Link to="/register">Unlock 40 more →</Link>
              </Button>
            </CardContent>
          </Card>
        )}

        {(l.share_prompt || tx?.share_prompt) && (
          <Card className="bg-sky-50 border-sky-200" data-testid="try-share-prompt-card">
            <CardHeader><CardTitle className="text-base">📸 Show it off</CardTitle></CardHeader>
            <CardContent>
              <Bilingual
                local={tx?.share_prompt}
                english={l.share_prompt}
                className="text-sm text-slate-700"
                testId="try-share-prompt-body"
              />
            </CardContent>
          </Card>
        )}

        <Card className="bg-emerald-600 text-white" data-testid="try-cta-card">
          <CardContent className="p-6 text-center space-y-3">
            <h3 className="text-2xl font-bold">{data.cta.headline}</h3>
            <p className="text-emerald-100">40 more lessons · Maths · English · Science · Scratch · Certificates</p>
            <Button asChild size="lg" className="bg-white text-emerald-700 hover:bg-emerald-50" data-testid="try-register-cta">
              <Link to="/register">Register for LKR {data.cta.price_lkr}/month →</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
